import React, { useEffect, useState } from 'react';
import { api, AED_short } from '../lib/api';
import { motion } from 'framer-motion';
import { Plus, Star, Users, KeyRound, Copy, Check, BarChart3, X, TrendingUp, ClipboardCheck, Receipt, ShieldAlert } from 'lucide-react';
import VendorModal from '../components/VendorModal';
import InvoiceModal from '../components/InvoiceModal';
import { useAuth } from '../lib/auth';
import { toast } from 'sonner';

export default function Vendors() {
  const { user: me } = useAuth();
  const isAdmin = me && ['owner', 'md'].includes(me.role);
  const [vendors, setVendors] = useState([]);
  const [scorecards, setScorecards] = useState({}); // vendor_id -> {score, band}
  const [teams, setTeams] = useState({}); // vendor_id -> {count, admin_count}
  const [adding, setAdding] = useState(false);
  const [viewV, setViewV] = useState(null);
  const [viewInv, setViewInv] = useState(null);
  const [scoreV, setScoreV] = useState(null); // vendor id to show scorecard modal for
  const [tempPw, setTempPw] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [f, setF] = useState({ name: '', company: '', trade: 'General Contracting', email: '', phone: '', trn: '' });

  const load = () => api.get('/vendors').then(r => {
    setVendors(r.data);
    if (isAdmin) {
      // Best-effort — load portal-team counts for each vendor
      Promise.all(r.data.map(v =>
        api.get(`/admin/vendors/${v.id}/team`).then(res => [v.id, res.data]).catch(() => [v.id, { count: 0, team: [] }])
      )).then(pairs => {
        const map = {};
        pairs.forEach(([id, data]) => {
          const active = (data.team || []).filter(u => u.is_active !== false);
          const admins = active.filter(u => u.is_vendor_admin);
          map[id] = { count: active.length, admin_count: admins.length };
        });
        setTeams(map);
      });
    }
  });
  const loadScores = () => api.get('/vendors/scorecards/all').then(r => {
    const map = {};
    (r.data || []).forEach(s => { map[s.vendor.id] = s; });
    setScorecards(map);
  }).catch(() => {});
  useEffect(() => { load(); loadScores(); }, []);

  const save = async (e) => {
    e.preventDefault();
    await api.post('/vendors', f);
    setAdding(false);
    setF({ name: '', company: '', trade: 'General Contracting', email: '', phone: '', trn: '' });
    load();
  };

  const createPortalAccount = async (e, v) => {
    e.stopPropagation();
    if (!v.email) { toast.error('Vendor needs an email first. Open the vendor and add one.'); return; }
    if (!window.confirm(`Create a Vendor Portal login for ${v.company} (${v.email})?\n\nA one-time temp password will be generated for you to share.`)) return;
    setBusyId(v.id);
    try {
      const r = await api.post(`/admin/vendors/${v.id}/create-account`, { email: v.email });
      setTempPw({ vendor: v, temp: r.data.temp_password, existed: r.data.existed });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally { setBusyId(null); }
  };

  return (
    <div className="py-6 max-w-6xl mx-auto" data-testid="vendors-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="overline">Directory</div>
          <h1 className="font-serif text-4xl">Vendors & contractors</h1>
        </div>
        <button onClick={()=>setAdding(true)} data-testid="add-vendor-btn" className="btn-primary text-sm flex items-center gap-2"><Plus size={14}/> Add vendor</button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {vendors.map(v => (
          <motion.div key={v.id} initial={{opacity:0}} animate={{opacity:1}}
                      onClick={() => setViewV(v.id)} data-testid={`vendor-card-${v.id}`}
                      className="glass p-5 lift cursor-pointer">
            <div className="flex items-start justify-between mb-2">
              <div>
                <div className="font-medium text-slate-900">{v.company}</div>
                <div className="text-xs text-slate-500">{v.name}</div>
              </div>
              <span className="status-pill status-gold text-xs">{v.trade}</span>
            </div>
            <div className="text-xs text-slate-600 space-y-1 mt-3 border-t border-slate-100/80 pt-3">
              <div>{v.email}</div>
              <div>{v.phone}</div>
              {v.trn && <div className="font-mono">TRN {v.trn}</div>}
            </div>
            {v.rating > 0 && (
              <div className="flex items-center gap-1 text-xs text-amber-600 mt-3">
                <Star size={12} fill="currentColor"/> {v.rating.toFixed(1)}
              </div>
            )}
            {scorecards[v.id] && (
              <button
                onClick={(e) => { e.stopPropagation(); setScoreV(v.id); }}
                data-testid={`vendor-scorecard-${v.id}`}
                className="w-full mt-3 pt-3 border-t border-slate-100/80 text-xs flex items-center justify-between hover:text-[#679286]"
              >
                <span className="flex items-center gap-1.5"><BarChart3 size={11}/> Performance</span>
                <span className={`font-serif text-base ${
                  scorecards[v.id].band === 'A' ? 'text-emerald-600' :
                  scorecards[v.id].band === 'B' ? 'text-blue-600' :
                  scorecards[v.id].band === 'C' ? 'text-amber-600' : 'text-red-500'
                }`}>
                  {Math.round(scorecards[v.id].score || 0)} <span className="text-[10px] opacity-60">· {scorecards[v.id].band}</span>
                </span>
              </button>
            )}
            {isAdmin && (
              <button
                onClick={(e) => createPortalAccount(e, v)}
                disabled={busyId === v.id}
                data-testid={`vendor-create-account-${v.id}`}
                className="w-full mt-3 pt-3 border-t border-slate-100/80 text-xs text-[#679286] hover:text-[#4d7266] flex items-center justify-between gap-1.5"
              >
                <span className="flex items-center gap-1.5">
                  <KeyRound size={11}/>
                  {teams[v.id]?.count > 0
                    ? `${teams[v.id].count} portal user${teams[v.id].count === 1 ? '' : 's'}`
                    : (busyId === v.id ? 'Generating…' : 'Create portal admin')}
                </span>
                {teams[v.id]?.count > 0 && (
                  <span className="inline-flex items-center gap-1 text-emerald-600 text-[10px] font-semibold uppercase tracking-widest">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"/> Active
                  </span>
                )}
                {(!teams[v.id] || teams[v.id]?.count === 0) && busyId !== v.id && (
                  <span className="text-[10px] text-slate-400 uppercase tracking-widest">No login yet</span>
                )}
              </button>
            )}
          </motion.div>
        ))}
        {vendors.length === 0 && (
          <div className="col-span-3 text-center py-16 text-slate-400 glass">
            <Users size={40} className="mx-auto mb-3 opacity-50"/>
            No vendors yet.
          </div>
        )}
      </div>

      {adding && (
        <div className="fixed inset-0 z-40 grid place-items-center p-4 bg-slate-900/30 backdrop-blur-sm" onClick={()=>setAdding(false)}>
          <form onClick={(e)=>e.stopPropagation()} onSubmit={save} className="glass-strong w-full max-w-md p-8">
            <div className="overline mb-2">New</div>
            <h2 className="font-serif text-2xl mb-4">Add vendor</h2>
            {['name', 'company', 'email', 'phone', 'trn'].map(k => (
              <label key={k} className="block text-sm mb-2 capitalize">{k}
                <input required={k === 'name' || k === 'company' || k === 'email'} value={f[k]} onChange={(e)=>setF({...f, [k]:e.target.value})}
                       data-testid={`vendor-${k}`}
                       className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 bg-white/80"/>
              </label>
            ))}
            <label className="block text-sm mb-4">Trade
              <select value={f.trade} onChange={(e)=>setF({...f, trade:e.target.value})} className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 bg-white/80">
                {['General Contracting','MEP','Joinery','Tiling & Finishes','Painting','HVAC','Plumbing','Electrical','Sanitary','Landscaping'].map(t=><option key={t}>{t}</option>)}
              </select>
            </label>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={()=>setAdding(false)} className="btn-ghost text-sm">Cancel</button>
              <button type="submit" className="btn-primary text-sm" data-testid="vendor-save">Save</button>
            </div>
          </form>
        </div>
      )}
      {viewV && <VendorModal vendorId={viewV} onClose={() => setViewV(null)}
                              onOpenInvoice={(id)=>setViewInv(id)}
                              onChanged={() => { load(); loadScores(); }}/>}
      {viewInv && <InvoiceModal invoiceId={viewInv} onClose={() => setViewInv(null)}/>}
      {scoreV && (
        <VendorScorecardModal
          vendorId={scoreV}
          vendor={vendors.find(v => v.id === scoreV)}
          onClose={() => setScoreV(null)}
        />
      )}

      {tempPw && (
        <div className="fixed inset-0 z-40 grid place-items-center p-4 bg-slate-900/40 backdrop-blur-sm">
          <motion.div initial={{opacity:0,y:12}} animate={{opacity:1,y:0}}
            className="glass-strong w-full max-w-md p-8" data-testid="vendor-temp-pw-modal">
            <div className="overline mb-1 text-amber-800">{tempPw.existed ? 'Password reset' : 'Vendor account created'}</div>
            <h2 className="font-serif text-2xl mb-3">{tempPw.vendor.company}</h2>
            <p className="text-sm text-slate-600 mb-4">
              Share these credentials with the vendor. They must change the password on first login.
              <br/><b>Portal URL:</b> <span className="font-mono text-xs">{window.location.origin}/vendor-portal</span>
            </p>
            <div className="text-xs text-slate-500 mb-1">Email</div>
            <div className="p-2 mb-3 bg-slate-100 rounded-lg font-mono text-sm">{tempPw.vendor.email}</div>
            <div className="text-xs text-slate-500 mb-1">Temp password (one-time)</div>
            <div className="flex items-center gap-2 p-3 bg-slate-900 text-white rounded-xl font-mono text-lg mb-4">
              <span className="flex-1" data-testid="vendor-temp-pw-value">{tempPw.temp}</span>
              <button onClick={async () => { await navigator.clipboard.writeText(tempPw.temp); toast.success('Copied'); }} className="p-1.5 rounded-lg hover:bg-white/10">
                <Copy size={14}/>
              </button>
            </div>
            <div className="flex justify-end">
              <button onClick={() => setTempPw(null)} className="btn-primary text-sm" data-testid="vendor-temp-pw-close">I've saved it</button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}

// ---------- Vendor Scorecard Modal ----------
function VendorScorecardModal({ vendorId, vendor, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/vendors/${vendorId}/scorecard`).then(r => setData(r.data)).finally(() => setLoading(false));
  }, [vendorId]);

  if (!vendor) return null;
  const bandColor = {
    A: 'bg-emerald-500', B: 'bg-blue-500', C: 'bg-amber-500', D: 'bg-red-500',
  }[data?.band] || 'bg-slate-400';

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center p-4 bg-slate-900/50 backdrop-blur-sm"
      onClick={onClose}
      data-testid="vendor-scorecard-modal"
    >
      <motion.div
        onClick={(e) => e.stopPropagation()}
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        className="glass-strong w-full max-w-2xl p-8 max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-center gap-3 mb-6">
          <div className={`w-14 h-14 rounded-full ${bandColor} grid place-items-center text-white font-serif text-2xl shadow-lg`}>
            {data?.band || '—'}
          </div>
          <div className="flex-1">
            <div className="overline">Performance scorecard</div>
            <h2 className="font-serif text-2xl">{vendor.company}</h2>
            <div className="text-xs text-slate-500">{vendor.trade}</div>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-slate-100" data-testid="vendor-scorecard-close">
            <X size={18}/>
          </button>
        </div>

        {loading ? (
          <div className="py-10 text-center text-slate-500 text-sm">Calculating…</div>
        ) : !data ? (
          <div className="py-10 text-center text-slate-500 text-sm">No performance data yet.</div>
        ) : (
          <>
            <div className="text-center mb-6">
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Overall score</div>
              <div className="font-serif text-6xl mt-1">{Math.round(data.score || 0)}<span className="text-2xl text-slate-400">/100</span></div>
              <div className="text-xs text-slate-500 mt-1">Band {data.band}</div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              <MetricTile icon={ClipboardCheck} label="Jobs completed" value={data.metrics?.completed_jobs || 0} accent="text-blue-600"/>
              <MetricTile icon={TrendingUp} label="On-time %" value={`${Math.round(data.metrics?.on_time_pct || 0)}%`} accent="text-emerald-600"/>
              <MetricTile icon={Receipt} label="Invoiced" value={AED_short(data.metrics?.total_invoiced_aed || 0)} accent="text-slate-800"/>
              <MetricTile icon={ShieldAlert} label="Open snags" value={data.metrics?.open_snags || 0} accent="text-red-500"/>
            </div>

            {data.metrics && (
              <div className="glass p-4">
                <div className="overline mb-3">Detail</div>
                <div className="space-y-2 text-sm">
                  {Object.entries(data.metrics).map(([k, v]) => (
                    <div key={k} className="flex justify-between">
                      <span className="text-slate-500 capitalize">{k.replace(/_/g, ' ')}</span>
                      <span className="font-medium">{typeof v === 'number' ? (v > 1000 ? v.toLocaleString() : v.toFixed?.(2) || v) : String(v)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {data.factors?.length ? (
              <div className="glass p-4 mt-3">
                <div className="overline mb-2">Score factors</div>
                <ul className="space-y-1 text-sm">
                  {data.factors.map((f, i) => (
                    <li key={i} className="flex justify-between">
                      <span className="text-slate-700">{f.label}</span>
                      <span className={f.delta > 0 ? 'text-emerald-600' : f.delta < 0 ? 'text-red-500' : 'text-slate-500'}>
                        {f.delta > 0 ? '+' : ''}{f.delta}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </>
        )}
      </motion.div>
    </div>
  );
}

function MetricTile({ icon: Icon, label, value, accent }) {
  return (
    <div className="rounded-xl bg-white/80 border border-white/60 p-3 text-center">
      <Icon size={14} className={`mx-auto mb-1 ${accent}`}/>
      <div className={`font-serif text-xl ${accent}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-0.5">{label}</div>
    </div>
  );
}
